import LZString from 'lz-string';
import { IS_NATIVE, CANONICAL_ORIGIN } from '../config.js';
import { STATE_VERSION, encodeFurniture, pickLayout } from '../utils/layoutCodec.js';
import { readLayout } from '../utils/orientationMigration.js';
import { UserPreferences } from './UserPreferences.js';
import { saveToHistory } from './DesignHistory.js';

/**
 * StateManager owns the *layout* (furniture + design options); UserPreferences owns the
 * per-browser *preferences* (environment, view toggles, floor crop). Both save through
 * saveSceneState(), so callers only ever need that one entry point.
 */
export class StateManager {
  constructor(app) {
    this.app = app;

    // localStorage keys
    this.SCENE_STATE_KEY = 'threejs_scene_state';
    // Left behind by the retired "Reset to Original Layout" action.
    if (!app?.isEmbedMode) {
      try { localStorage.removeItem('threejs_original_layout'); } catch { /* storage unavailable */ }
    }

    this.preferences = new UserPreferences(app);

    // Serialized-format version. '2' = compact layout (see layoutCodec.js); older
    // payloads ('1.0') are still read via the decode passthrough.
    this.STATE_VERSION = STATE_VERSION;

    // Named-preset tracking. When the scene is an unedited preset loaded from
    // layouts.json, the address bar / share link use the short `?preset=<id>` form
    // instead of the full compressed `?layout=` blob. The baseline signature is the
    // layout-defining slice of the state captured at load; the first edit that changes
    // it drops us back to the compressed form. See markAsPreset / _presetSignature.
    this._activePresetId = null;
    this._presetBaselineSignature = null;
    // The preset the scene was loaded from, kept across edits (unlike _activePresetId,
    // which the first edit drops). path-tracing.json declares rigs per preset, and a
    // nudged module shouldn't throw the shot back to the environment's rig.
    this._loadedPresetId = null;
  }

  /**
   * Record which layouts.json preset the scene was loaded from — null when starting from
   * scratch or restoring a session. Read by PathTracingLightRig for per-preset rigs, which
   * pulls this from the render loop — no invalidate needed here.
   * @param {string|null} presetId
   */
  setLoadedPreset(presetId) {
    this._loadedPresetId = presetId || null;
  }

  getLoadedPresetId() {
    return this._loadedPresetId;
  }

  /**
   * Signature of the layout-defining fields only, so browsing the catalog or moving the camera
   * doesn't invalidate a short `?preset=` link — nor does a display toggle, now a preference.
   * @param {Object} state
   * @returns {string}
   */
  _presetSignature(state) {
    if (!state) return '';
    return JSON.stringify({
      furniture: state.furniture ?? null,
      legTexture: state.legTexture ?? null,
      legHeight: state.legHeight ?? null,
      covers: state.covers ?? null,
    });
  }

  /**
   * Mark the current scene as the unedited named preset `presetId` (an id from
   * layouts.json combinations). Captures the baseline layout signature and immediately
   * reflects the short `?preset=<id>` form in the address bar.
   * @param {string} presetId
   */
  markAsPreset(presetId) {
    this._activePresetId = presetId || null;
    this._presetBaselineSignature = this._presetSignature(this.getSceneStateObject());
    // Pre-empt any pending compressed-URL sync and show the short form now.
    clearTimeout(this._urlSyncTimer);
    if (this._activePresetId) {
      // URL rewrites keep the entry's state: the layout picker marks its history entries there.
      window.history.replaceState(window.history.state, '', this._presetURL(this._historyBase()));
    }
  }

  /** Forget any active preset so URLs revert to the compressed `?layout=` form. */
  clearActivePreset() {
    this._activePresetId = null;
    this._presetBaselineSignature = null;
  }

  /**
   * Local origin + path. The ONLY base history.replaceState may be given — rewriting
   * history to a different origin throws a SecurityError, which is exactly what would
   * happen natively if this returned the canonical share origin.
   */
  _historyBase() {
    return window.location.origin + window.location.pathname;
  }

  /**
   * Public base for anything a person or another device will open. On the web that is
   * the page itself; natively `window.location` is `capacitor://localhost`, which nobody
   * can open, so links are built against the canonical origin instead.
   */
  _shareBase() {
    return IS_NATIVE ? `${CANONICAL_ORIGIN}/` : this._historyBase();
  }

  /**
   * Query params carried across a URL rewrite: everything except the mutually-exclusive
   * state carriers (`preset`/`layout`). Preserves `?embed=1` and the load-time overrides
   * (`fabric`/`legColor`/`legHeight`/`boardColor`/`element`) so collapsing to the short `?preset=` form
   * — or generating a share link — doesn't silently drop them.
   */
  _carryOverParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('preset');
    params.delete('layout');
    return params;
  }

  /**
   * Short `?preset=<id>` URL for the active preset (keeps embed/overrides).
   * Defaults to the shareable base; callers rewriting history must pass the local one.
   */
  _presetURL(base = this._shareBase()) {
    const params = this._carryOverParams();
    params.set('preset', this._activePresetId);
    return `${base}?${params.toString()}`;
  }

  /** True while the scene still matches the named preset it was loaded from. */
  _isUneditedPreset(state) {
    return !!this._activePresetId && this._presetSignature(state) === this._presetBaselineSignature;
  }

  /**
   * The current layout as a serializable object: furniture plus the design's own options.
   * Environment and view toggles are deliberately absent — see UserPreferences.
   * @returns {Object} Layout state
   */
  getSceneStateObject() {
    try {
      const furnitureManager = this.app.getFurnitureManager();
      const cameraManager = this.app.getCameraManager();
      const uiManager = this.app.getUIManager();

      // Compact the verbose serializeLayout() output (short keys, int ids, rounded
      // floats) so the localStorage copy, share URL and presets all stay small.
      const state = {
        version: this.STATE_VERSION,
        furniture: encodeFurniture(furnitureManager.serializeLayout()),
        collection: uiManager.currentCategory, // Save current collection
      };

      // Design options: fabric lives per item, these are scene-wide.
      if (furnitureManager) {
        state.legTexture = furnitureManager.currentLegTextureType;
        state.legHeight = furnitureManager.currentLegHeight;
        state.covers = furnitureManager.currentCovers;
      }

      // Framing is part of the shared design (a preset opens on its own view).
      if (cameraManager) {
        state.camera = cameraManager.getCameraState();
      }

      return state;
    } catch (error) {
      console.error('Failed to get scene state:', error);
      return null;
    }
  }

  /**
   * Validate layout structure. Room-size ranges moved to UserPreferences.validate().
   * @param {*} state
   * @returns {boolean}
   */
  _validateState(state) {
    if (!state || typeof state !== 'object') return false;
    if (!state.version) return false;
    if (state.furniture !== undefined && !Array.isArray(state.furniture)) return false;
    return true;
  }

  /**
   * Debounced save — any manager can call this instead of reaching into FurnitureManager.
   * The timeout is reset on every call; the actual save fires 1 second after the last call.
   */
  debouncedSave() {
    if (this._saveTimeout) clearTimeout(this._saveTimeout);
    this._saveTimeout = setTimeout(() => {
      this.saveSceneState();
    }, 1000);
  }

  /** Save the layout and the user's preferences to localStorage — the one entry point for both. */
  saveSceneState() {
    // Before the guards: an embed or a load still changes the dimensions and the free attachment points.
    this.app?.getDimensionManager?.()?.invalidate();
    this.app?.getFurnitureManager?.()?.invalidateAttachmentMarkers?.();
    // Read-only embeds must never write to the host's storage. This is the single
    // chokepoint every save path funnels through (including EnvironmentManager's
    // own changeEnvironment save), so the guard belongs here rather than at callers.
    if (this.app?.isEmbedMode) return false;
    if (this._isLoading) return false;
    try {
      const state = this.getSceneStateObject();
      if (!state) return false;

      localStorage.setItem(this.SCENE_STATE_KEY, JSON.stringify(state));
      this.preferences.save();
      this._scheduleSyncURL(state);
      // Piggy-backing on the save chokepoint: every settings toggle and committed
      // edit reaches here, and an accumulated trace is stale the moment any of them
      // lands. The call is debounced and no-ops unless path tracing is on.
      this.app?.getPathTracingManager?.()?.invalidate();
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Cannot save scene state.');
        // Could show user warning here
      } else {
        console.error('Failed to save scene state:', error);
      }
      return false;
    }
  }

  /**
   * Apply this browser's stored preferences at startup, before any layout loads, so furniture
   * spawns into the room the user left behind. Skipped in embed mode, which forces its own
   * presentation and must not inherit the host browser's setup.
   * @returns {Boolean} whether stored preferences were found
   */
  applyPreferences() {
    if (this.app?.isEmbedMode) return false;
    // Applying fires manager callbacks that would otherwise save a half-built scene.
    this._isLoading = true;
    try {
      return this.preferences.apply();
    } finally {
      this._isLoading = false;
    }
  }

  /**
   * Load complete scene state from localStorage
   * @returns {Boolean} Success status
   */
  loadSceneState() {
    try {
      // Restoring a saved/edited session is not a pristine preset.
      this.clearActivePreset();
      const saved = localStorage.getItem(this.SCENE_STATE_KEY);
      if (!saved) {
        return false;
      }

      const state = JSON.parse(saved);

      if (!this._validateState(state)) {
        console.warn('Invalid scene state structure');
        return false;
      }

      return this.loadSceneFromObject(state);
    } catch (error) {
      console.error('Failed to load scene state:', error);
      return false;
    }
  }

  /**
   * Apply a layout from a state object; any environment / view-toggle fields a legacy payload
   * still carries are ignored (see applyPreferences).
   * @param {Object} rawState - Layout state object
   * @returns {Boolean} Success status
   */
  async loadSceneFromObject(rawState) {
    try {
      const state = pickLayout(rawState);
      const furnitureManager = this.app.getFurnitureManager();
      const dimensionManager = this.app.getDimensionManager();
      const cameraManager = this.app.getCameraManager();
      const uiManager = this.app.getUIManager();

      // Suppress state saving during load to avoid overwriting with incomplete state
      this._isLoading = true;

      // Clear selection UI before loading new layout
      uiManager?.updateSelection(null);

      // Restore leg height BEFORE loadLayout so each spawnFurniture call inside
      // loadLayout picks up the right currentLegHeight and applies it to the
      // newly-spawned mesh. Item positions in the saved layout are LOGICAL
      // (the lift was subtracted in serializeLayout), so applyLegHeight inside
      // spawnFurniture re-applies the body lift.
      if (state.legHeight && furnitureManager) {
        furnitureManager.currentLegHeight = state.legHeight;
      }

      // Verbose items in the current frame; a pre-+Z-front layout (version < 3) is migrated.
      const layout = readLayout(state, (item) => furnitureManager?.findCatalogItem(item));
      if (state.furniture && Array.isArray(state.furniture)) {
        await furnitureManager.loadLayout(layout.items);
      }

      // Restore leg texture setting
      if (state.legTexture && furnitureManager) {
        furnitureManager.updateAllLegTextures(state.legTexture);
      }

      // Restore covers (cart-only per-group map); ignore legacy non-object values.
      if (state.covers && typeof state.covers === 'object' && furnitureManager) {
        furnitureManager.currentCovers = { ...state.covers };
      }

      // Update leg height dropdown + covers UI based on loaded furniture
      uiManager?.updateLegHeightDropdown();
      uiManager?.updateCoversUI();

      // Dimension labels track the furniture that just loaded.
      dimensionManager?.update();

      // Restore camera state
      if (layout.camera && cameraManager) {
        cameraManager.setCameraState(layout.camera);
      }

      // The floor crop clamps to the furniture bounds, so it can only settle once the layout is in.
      this.preferences.applyRoomBox();

      // Restore collection filter. Skipped in embed — the grid UI isn't built and
      // setFurnitureCollectionFilter would regenerate catalog thumbnails into it.
      if (state.collection && uiManager && !this.app.isEmbedMode) {
        uiManager.setFurnitureCollectionFilter(state.collection, false);
      }

      // Sync all UI controls to match restored state (DOM updates centralised in UIManager)
      uiManager?.syncStateToUI(state);

      this._isLoading = false;
      return true;
    } catch (error) {
      this._isLoading = false;
      console.error('Failed to load scene from object:', error);
      return false;
    }
  }

  /**
   * Generate shareable URL with compressed scene state
   * @returns {String} Full URL with layout parameter
   */
  _scheduleSyncURL(state) {
    clearTimeout(this._urlSyncTimer);
    this._urlSyncTimer = setTimeout(() => {
      try {
        // Keep the short `?preset=<id>` form while the scene still matches the loaded
        // preset; the first layout edit changes the signature and drops to compressed.
        if (this._isUneditedPreset(state)) {
          window.history.replaceState(window.history.state, '', this._presetURL(this._historyBase()));
          return;
        }
        this.clearActivePreset();
        window.history.replaceState(window.history.state, '', `${this._historyBase()}?layout=${this.getCompressedLayout(state)}`);
      } catch (e) {
        console.warn('Failed to sync state to URL:', e);
      }
    }, 1000);
  }

  /** A layout in the `?layout=` encoding — the current one by default. */
  getCompressedLayout(state = this.getSceneStateObject()) {
    return state ? LZString.compressToEncodedURIComponent(JSON.stringify(state)) : null;
  }

  /** Keep the current layout in the design history; null in embed mode or when storage refused it. */
  saveDesign({ name, thumbnail }) {
    if (this.app?.isEmbedMode) return null;
    const layout = this.getCompressedLayout();
    return layout ? saveToHistory({ layout, name, thumbnail }) : null;
  }

  /** Open a saved design: a share link kept on this device, not a preset. */
  async loadDesign(layout) {
    const loaded = await this.loadFromURL(layout);
    this.setLoadedPreset(null);
    return loaded;
  }

  hasSavedSession() {
    try { return localStorage.getItem(this.SCENE_STATE_KEY) !== null; } catch { return false; }
  }

  generateShareableURL() {
    try {
      const state = this.getSceneStateObject();
      if (!state) {
        console.error('Failed to generate shareable URL: could not get scene state');
        return null;
      }

      // Unedited named preset → share the short `?preset=<id>` link.
      if (this._isUneditedPreset(state)) {
        return this._presetURL();
      }

      // Otherwise serialize + compress into the full `?layout=` link.
      return `${this._shareBase()}?layout=${this.getCompressedLayout(state)}`;
    } catch (error) {
      console.error('Failed to generate shareable URL:', error);
      return null;
    }
  }

  /**
   * Load scene state from URL parameter
   * @param {String} urlParam - Compressed layout parameter from URL
   * @returns {Boolean} Success status
   */
  async loadFromURL(urlParam) {
    try {
      // A compressed `?layout=` payload is not, by itself, a named preset. The named
      // preset path (UIManager.selectLayout) re-marks it via markAsPreset() afterwards.
      this.clearActivePreset();
      if (!urlParam) {
        console.warn('No URL parameter provided');
        return false;
      }

      // Decompress
      const decompressed = LZString.decompressFromEncodedURIComponent(urlParam);
      if (!decompressed) {
        console.error('Failed to decompress URL parameter');
        return false;
      }

      // Parse JSON
      const state = JSON.parse(decompressed);

      if (!this._validateState(state)) {
        console.error('Invalid state structure from URL');
        return false;
      }

      const success = await this.loadSceneFromObject(state);

      if (success) {
        // Optionally save to localStorage for convenience
        this.saveSceneState();
      }

      return success;
    } catch (error) {
      console.error('Failed to load from URL:', error);
      const ui = this.app.getUIManager();
      ui?.showNotification(ui.t('designerNotifySharedLayoutFailed', 'Shared layout could not be loaded.'), 'warning');
      // Fallback to localStorage
      return this.loadSceneState();
    }
  }

  destroy() {
    if (this._saveTimeout) clearTimeout(this._saveTimeout);
    if (this._urlSyncTimer) clearTimeout(this._urlSyncTimer);
  }

  /**
   * Get the saved layout from localStorage (without applying it)
   * @returns {Object|null} Saved layout or null
   */
  getSavedState() {
    try {
      const saved = localStorage.getItem(this.SCENE_STATE_KEY);
      if (!saved) return null;

      const state = JSON.parse(saved);

      if (!this._validateState(state)) {
        return null;
      }

      return pickLayout(state);
    } catch (error) {
      console.warn('Failed to get saved state:', error);
      return null;
    }
  }
}
